import { Injectable, Signal, WritableSignal, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRouteSnapshot, NavigationEnd, Router, Event as RouterEvent, UrlSegment } from '@angular/router';

import { filter } from 'rxjs/operators';

export interface IPageBreadcrumb {
    readonly label: string;
    readonly link?: string;
}

/**
 * Derives the page-header breadcrumb trail from the router, mirroring the
 * route-data approach: each route segment that declares `data.breadcrumb`
 * contributes one crumb, with its URL accumulated along the way. The trail is
 * always rooted at Home. Routes without `breadcrumb` data simply add nothing,
 * so pages that never render `sc-page-header` cost nothing here.
 */
@Injectable({ providedIn: 'root' })
export class PageHeaderService {
    readonly #router: Router = inject(Router);

    readonly #trail: WritableSignal<readonly IPageBreadcrumb[]> = signal<readonly IPageBreadcrumb[]>([]);
    readonly #leaf: WritableSignal<IPageBreadcrumb | null> = signal<IPageBreadcrumb | null>(null);

    public readonly breadcrumbs: Signal<readonly IPageBreadcrumb[]> = computed((): readonly IPageBreadcrumb[] => {
        const leaf: IPageBreadcrumb | null = this.#leaf();
        const trail: readonly IPageBreadcrumb[] = this.#trail();
        return leaf ? [...trail, leaf] : trail;
    });

    constructor() {
        this.#rebuild();
        this.#router.events
            .pipe(
                filter((event: RouterEvent): event is NavigationEnd => event instanceof NavigationEnd),
                takeUntilDestroyed()
            )
            .subscribe((): void => {
                // A new navigation invalidates any leaf the previous page set.
                this.#leaf.set(null);
                this.#rebuild();
            });
    }

    /**
     * Sets the deepest crumb for routes whose label no static route data can
     * know (e.g. the article title behind `/blog/:slug`). Cleared automatically
     * on the next navigation; pass null to drop it explicitly.
     */
    public setLeaf(label: string | null): void {
        this.#leaf.set(label ? { label } : null);
    }

    #rebuild(): void {
        const home: IPageBreadcrumb = { label: $localize`:@@navHome:Главная`, link: '/' };
        const collected: IPageBreadcrumb[] = this.#collect(this.#router.routerState.snapshot.root, '');
        // Dedup by URL — guards against Angular propagating the same breadcrumb
        // to empty-path child routes, which would otherwise repeat a crumb.
        const seen: Set<string> = new Set<string>();
        const trail: IPageBreadcrumb[] = [home, ...collected].filter((crumb: IPageBreadcrumb): boolean => {
            const key: string = crumb.link ?? crumb.label;
            if (seen.has(key)) {
                return false;
            }
            seen.add(key);
            return true;
        });
        this.#trail.set(trail);
    }

    #collect(route: ActivatedRouteSnapshot, url: string): IPageBreadcrumb[] {
        const segment: string = route.url.map((part: UrlSegment): string => part.path).join('/');
        const nextUrl: string = segment ? `${url}/${segment}` : url;
        // Read the route's OWN declared data, not the inherited snapshot data:
        // Angular propagates `data` down to empty-path index/child routes, which
        // would otherwise duplicate a parent crumb on the routes beneath it.
        const config: { breadcrumb?: unknown } | undefined = route.routeConfig?.data;
        const label: unknown = config?.breadcrumb;
        const crumbs: IPageBreadcrumb[] = typeof label === 'string' ? [{ label, link: nextUrl }] : [];
        return route.firstChild ? [...crumbs, ...this.#collect(route.firstChild, nextUrl)] : crumbs;
    }
}
